import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import { LifeCycleStage, OnboardingStep } from '@repo/shared/dtos/organization'
import {
  OrganizationsRoutes,
  UpdateStructuredDataDisplay,
  UpdateStructuredDataSchema,
} from '@repo/shared/routes-organizations'
import { SudoRoutes } from '@repo/shared/routes-sudo'
import { AxiosInstance } from 'axios'

import {
  buildCaseWorkerFromDto,
  buildOrganizationFromDto,
  buildSudoContextOrganizationFromDto,
} from '@/external/api/helpers'
import { mockedData } from '@/store/mock/mock'
import { IApi } from '@/store/spi/api'
import { ISudoApi } from '@/store/spi/sudo'

type T = {
  admin: IApi['admin']['organizations']
  app: IApi['organizations']
  sudo: ISudoApi['organizations']
}

export const apiOrganizations: (api?: AxiosInstance) => T = (api): T => {
  return {
    //
    // ADMIN
    //
    admin: {
      updateStructuredDataSchema: api
        ? async ({ organizationId, structuredDataSchema }) => {
            const res = await tryCatch(
              async () =>
                await api.put<typeof UpdateStructuredDataSchema.response>(
                  UpdateStructuredDataSchema.getPath({ organizationId }),
                  structuredDataSchema satisfies typeof UpdateStructuredDataSchema.request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({ success: true })
          },

      updateStructuredDataDisplay: api
        ? async ({ organizationId, structuredDataDisplay }) => {
            const res = await tryCatch(
              async () =>
                await api.put<typeof UpdateStructuredDataDisplay.response>(
                  UpdateStructuredDataDisplay.getPath({ organizationId }),
                  structuredDataDisplay satisfies typeof UpdateStructuredDataDisplay.request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({ success: true })
          },

      createOneFromAssistant: api
        ? async (fields) => {
            const res = await tryCatch(
              async () =>
                await api.post<OrganizationsRoutes.createOneFromAssistant.Response>(
                  OrganizationsRoutes.createOneFromAssistant.call(),
                  fields satisfies OrganizationsRoutes.createOneFromAssistant.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return {
              caseWorker: buildCaseWorkerFromDto(res.caseWorker),
              organization: buildOrganizationFromDto(res.organization),
            }
          }
        : (p) => {
            return Promise.resolve({
              organization: {
                ...mockedData.organization,
                name: p.name,
                url: p.url,
                onboarding: { step: OnboardingStep.ORGANIZATION },
              },
              caseWorker: mockedData.caseWorker,
            })
          },

      generateDescriptionForOne: api
        ? async (organizationId) => {
            const res = await tryCatch(
              async () =>
                await api.get<OrganizationsRoutes.generateOrganizationDescription.Response>(
                  OrganizationsRoutes.generateOrganizationDescription.call({
                    organizationId,
                  }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : (organizationId) => {
            const organization = mockedData.organizations.find((o) => o.id === organizationId)
            if (organization) return Promise.resolve({ description: organization.description })
            return Promise.resolve(unknownError)
          },

      generateMethodologyForOne: api
        ? async (organizationId) => {
            const res = await tryCatch(
              async () =>
                await api.get<OrganizationsRoutes.generateOrganizationMethodology.Response>(
                  OrganizationsRoutes.generateOrganizationMethodology.call({
                    organizationId,
                  }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : (organizationId) => {
            const organization = mockedData.organizations.find((o) => o.id === organizationId)
            if (organization) return Promise.resolve({ methodology: organization.methodology })
            return Promise.resolve(unknownError)
          },

      patchOne: api
        ? async ({ fields, organizationId }) => {
            const res = await tryCatch(
              async () =>
                await api.patch<OrganizationsRoutes.patchOne.Response>(
                  OrganizationsRoutes.patchOne.call({ organizationId }),
                  { fields } satisfies OrganizationsRoutes.patchOne.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return buildOrganizationFromDto(res.organization)
          }
        : (p) => {
            return Promise.resolve({
              ...mockedData.organizations[0]!,
              ...p.fields,
            })
          },
    },

    //
    // SUDO
    //
    sudo: {
      patchOne: api
        ? async ({ organizationId, fields }) => {
            const res = await tryCatch(
              async () =>
                await api.patch<SudoRoutes.organizations.patchOne.Response>(
                  SudoRoutes.organizations.patchOne.call({ organizationId }),
                  { fields } satisfies SudoRoutes.organizations.patchOne.Request,
                ),
            )

            if (hasError(res)) return handleError(res)
            return res.organizations.map(buildSudoContextOrganizationFromDto)
          }
        : (p) => {
            return Promise.resolve(
              mockedData.sudo.organizations.map((o) => {
                if (o.id === p.organizationId) {
                  return {
                    ...o,
                    ...p.fields,
                  }
                }
                return o
              }),
            )
          },

      createOne: api
        ? async (p) => {
            const res = await tryCatch(
              async () =>
                await api.post<SudoRoutes.organizations.createOne.Response>(
                  SudoRoutes.organizations.createOne.call(),
                  p satisfies SudoRoutes.organizations.createOne.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res.organizations.map(buildSudoContextOrganizationFromDto)
          }
        : (p) => {
            return Promise.resolve([
              ...mockedData.sudo.organizations,
              {
                name: p.name,
                id: 'newOrg',
                featureFlags: [],
                lifeCycleStage: LifeCycleStage.ONBOARDING,
              },
            ])
          },
      hardDeleteOne: api
        ? async ({ organizationId }) => {
            const res = await tryCatch(
              async () =>
                await api.delete<SudoRoutes.organizations.hardDeleteOne.Response>(
                  SudoRoutes.organizations.hardDeleteOne.call({ organizationId }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : ({ organizationId }) => {
            return Promise.resolve({
              success: !!organizationId,
              errorMessage: '',
            })
          },
    },

    //
    // APP
    //
    app: {
      get: api
        ? async () => {
            const res = await tryCatch(
              async () =>
                await api.get<OrganizationsRoutes.get.Response>(OrganizationsRoutes.get.call()),
            )
            if (hasError(res)) return handleError(res)
            return { organizations: res.organizations.map(buildOrganizationFromDto) }
          }
        : () => {
            return Promise.resolve({
              organizations: mockedData.organizations,
            })
          },

      getOne: api
        ? async (organizationId) => {
            const res = await tryCatch(
              async () =>
                await api.get<OrganizationsRoutes.getOne.Response>(
                  OrganizationsRoutes.getOne.call({ organizationId }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return buildOrganizationFromDto(res.organization)
          }
        : (organizationId) => {
            const organization = mockedData.organizations.find((o) => o.id === organizationId)
            if (organization) return Promise.resolve(organization)
            return Promise.resolve(unknownError)
          },
    },
  }
}
